import {
  Overlay,
  OverlayConfig,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';

import { SignupTooltipComponent } from './signup-tooltip.component';
import { TooltipArrowComponent } from './tooltip-arrow/tooltip-arrow.component';

@Directive({ selector: '[signupTooltip]' })
export class SignupTooltipDirective implements OnInit, OnChanges, OnDestroy {
  @Input('signupTooltip') text = '';
  @Input() title = '';
  @Input() position = 'bottom';
  @Input() tooltipFocused = false;

  private overlayRef: OverlayRef;
  private arrowOverlayRef: OverlayRef;

  constructor(private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef) { }

  @HostListener('mouseover')
  show() {
    if ((this.text || this.title) && !this.overlayRef.hasAttached() && !this.arrowOverlayRef.hasAttached()) {
      const tooltipPortal = new ComponentPortal(SignupTooltipComponent);
      const arrowPortal = new ComponentPortal(TooltipArrowComponent);
      const tooltipRef: ComponentRef<SignupTooltipComponent> = this.overlayRef.attach(tooltipPortal);
      this.arrowOverlayRef.attach(arrowPortal);
      tooltipRef.instance.text = this.text;
      tooltipRef.instance.title = this.title;
      tooltipRef.instance.originElementX = this.elementRef.nativeElement.x;
      tooltipRef.instance.originElementWidth = this.elementRef.nativeElement.width;
    }
  }

  @HostListener('mouseout')
  hide() {
    this.overlayRef.detach();
    this.arrowOverlayRef.detach();
  }

  ngOnInit() {
    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions([
        {
          originX: 'center',
          originY: this.position === 'bottom' ? 'bottom' : 'top',
          overlayX: 'center',
          overlayY: this.position === 'bottom' ? 'top' : 'bottom',
          panelClass: this.position === 'bottom' ? 'signup-tooltip--bottom' : 'signup-tooltip--top'
        },
        {
          originX: 'center',
          originY: this.position === 'bottom' ? 'top' : 'bottom',
          overlayX: 'center',
          overlayY: this.position === 'bottom' ? 'bottom' : 'top',
          panelClass: this.position === 'bottom' ? 'signup-tooltip--top' : 'signup-tooltip--bottom'
        },
      ]);

    const configArrowOverlay: OverlayConfig = {
      positionStrategy:
        this.overlay
          .position()
          .flexibleConnectedTo(this.elementRef)
          .withPositions([
            {
              originX: 'start',
              originY: 'bottom',
              overlayX: 'start',
              overlayY: 'top'
            }
          ])
    };

    positionStrategy.positionChanges.subscribe((res) => {
      const updatedArrowOverlayPositionStrategy =
        this.overlay
          .position()
          .flexibleConnectedTo(this.elementRef)
          .withPositions([
            {
              originX: res.connectionPair.originX,
              originY: res.connectionPair.originY,
              overlayX: res.connectionPair.overlayX,
              overlayY: res.connectionPair.overlayY,
              panelClass: res.connectionPair.overlayY === 'bottom' ? 'signup-tooltip__arrow--bottom' : 'signup-tooltip__arrow--top'
            }
          ]);
      this.arrowOverlayRef.updatePositionStrategy(updatedArrowOverlayPositionStrategy);
    });

    this.arrowOverlayRef = this.overlay.create(configArrowOverlay);
    this.overlayRef = this.overlay.create({ positionStrategy });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.tooltipFocused && changes.tooltipFocused.previousValue !== undefined) {
      if (changes.tooltipFocused.currentValue) {
        this.show();
      } else {
        this.overlayRef.detach();
        this.arrowOverlayRef.detach();
      }
    }
  }

  ngOnDestroy() {
    if (this.overlayRef.hasAttached() || this.arrowOverlayRef.hasAttached()) {
      this.hide();
    }
  }
}
